import { useCallback, useEffect, useState } from 'react'

import isIOS from 'Utils/isIOS'

const DEFAULT_CART = {
  isSaleOff: false,
  isCLDDisabled: true,
  itemIds: [],
  value: 0,
  applicableCldValue: 0,
  creditsAvailable: 0,
  cartItemIds: [],
}

const DEFAULT_CARD_VISIBILITY_SETTINGS = {
  title: true,
  type: true,
  price: true,
  format: true,
  quickActionsOnHover: true,
  cgtStandard: true,
}

function getBooleanValue(value, defaultValue) {
  return typeof value === 'boolean' ? value : defaultValue
}

function getCardVisibilitySettingsFromStorage() {
  if (typeof window === 'undefined') {
    return DEFAULT_CARD_VISIBILITY_SETTINGS
  }

  try {
    const cardVisibility = localStorage.getItem('cardVisibility')
    if (!cardVisibility) {
      return DEFAULT_CARD_VISIBILITY_SETTINGS
    }

    const parsedValue = JSON.parse(cardVisibility)

    return {
      title: getBooleanValue(parsedValue.title, DEFAULT_CARD_VISIBILITY_SETTINGS.title),
      type: getBooleanValue(parsedValue.type, DEFAULT_CARD_VISIBILITY_SETTINGS.type),
      price: getBooleanValue(parsedValue.price, DEFAULT_CARD_VISIBILITY_SETTINGS.price),
      format: getBooleanValue(parsedValue.format, DEFAULT_CARD_VISIBILITY_SETTINGS.format),
      quickActionsOnHover:
        getBooleanValue(parsedValue.quickActionsOnHover, DEFAULT_CARD_VISIBILITY_SETTINGS.quickActionsOnHover),
      cgtStandard: getBooleanValue(parsedValue.cgtStandard, DEFAULT_CARD_VISIBILITY_SETTINGS.cgtStandard),
    }
  } catch (error) {
    return DEFAULT_CARD_VISIBILITY_SETTINGS
  }
}

function useCollectionGrid() {
  // Cart state
  const [cart, setCart] = useState(DEFAULT_CART)
  const [cartItemIds, setCartItemIds] = useState([])

  // Wishlist state
  const [wishListItems, setWishListItems] = useState([])

  // Gallery state
  const [showGallery, setShowGallery] = useState(null)

  // Adult content modal state
  const [isAdultModalVisible, setIsAdultModalVisible] = useState(false)
  const [viewerIsAdult, setViewerIsAdult] = useState(false)

  // iOS detection
  const [isIOSDevice, setIsIOSDevice] = useState(false)

  // Card visibility settings from localStorage
  const [cardVisibilitySettings, setCardVisibilitySettings] = useState(DEFAULT_CARD_VISIBILITY_SETTINGS)

  // Fetch wishlist from API
  const getWishListItems = useCallback(async () => {
    try {
      const wishlist = await window.fetchJSON('/api/internal/wish-lists/default')
      if (wishlist.data.length > 0) {
        const ids = wishlist.data
          .map((item) => item?.attributes?.itemId)
          .filter(Boolean)
        setWishListItems(ids)
      }
    } catch (error) {
      // Silently fail - user might not be logged in
    }
  }, [])

  // Sync cartItemIds when cart changes
  useEffect(() => {
    if (cart.cartItemIds) {
      setCartItemIds(cart.cartItemIds)
    }
  }, [cart])

  // Initialize from pageConfig and set up event listeners
  useEffect(() => {
    setIsIOSDevice(isIOS())
    setCardVisibilitySettings(getCardVisibilitySettingsFromStorage())

    function readPageConfig() {
      window.removeEventListener('window-config-loaded', readPageConfig)

      if (window?.pageConfig?.viewerIsAdult) {
        setViewerIsAdult(true)
      }

      if (window?.pageConfig?.cart) {
        setCart(window.pageConfig.cart)
      }

      if (window?.pageConfig?.cart_items) {
        const items = window.pageConfig.cart_items.map((item) => ({
          cartId: item.cart_item_id,
          itemId: item.id,
        }))
        setCartItemIds(items)
      }

      if (window?.pageConfig?.currentUserId) {
        getWishListItems()
      }
    }

    window.addEventListener('window-config-loaded', readPageConfig)

    // If pageConfig already loaded before this effect ran, fire the handler now.
    if (window?.pageConfig) {
      window.dispatchEvent(new CustomEvent('window-config-loaded'))
    }

    return () => {
      window.removeEventListener('window-config-loaded', readPageConfig)
    }
  }, [getWishListItems])

  // Listen for cart loaded event
  useEffect(() => {
    function handleCartLoaded(event) {
      setCart(event.detail || DEFAULT_CART)
    }

    window.addEventListener('navigation-cart-loaded', handleCartLoaded)

    return () => {
      window.removeEventListener('navigation-cart-loaded', handleCartLoaded)
    }
  }, [])

  // Listen for item removed from cart event
  useEffect(() => {
    function handleItemRemovedFromCart() {
      const currentCartItemIds = (window?.pageConfig?.cart_items || []).map(({ id }) => id)
      setCartItemIds((state) => {
        const itemToBeRemoved = state.find(({ itemId }) => !currentCartItemIds.includes(itemId))
        return state.filter(({ itemId }) => itemId !== itemToBeRemoved?.itemId)
      })
    }

    window.addEventListener('item-removed-from-cart', handleItemRemovedFromCart)

    return () => {
      window.removeEventListener('item-removed-from-cart', handleItemRemovedFromCart)
    }
  }, [])

  // Gallery handlers
  const handlePreviewClick = useCallback((props) => {
    setShowGallery(null)
    // Use setTimeout to ensure state is cleared before setting new value
    setTimeout(() => setShowGallery(props), 0)
  }, [])

  const handleCloseGallery = useCallback(() => {
    setShowGallery(null)
  }, [])

  // Adult modal handlers
  const handleShowAdultModal = useCallback(() => {
    setIsAdultModalVisible(true)
  }, [])

  const handleHideAdultModal = useCallback(() => {
    setIsAdultModalVisible(false)
  }, [])

  return {
    // Cart
    cart,
    cartItemIds,
    setCartItemIds,

    // Wishlist
    wishListItems,
    setWishListItems,

    // Gallery
    showGallery,
    handlePreviewClick,
    handleCloseGallery,

    // Adult content
    viewerIsAdult,
    setViewerIsAdult,
    isAdultModalVisible,
    handleShowAdultModal,
    handleHideAdultModal,

    // Device detection
    isIOSDevice,

    // Card visibility settings
    cardVisibilitySettings,
  }
}

export default useCollectionGrid
